/**
 * Impression d'une SKILLCARD au format carte à jouer 63 × 88 mm (spec § 17) — route
 * `/tutor/cards/$cardId/print`, enfant direct de `appRoute` (hors layout
 * tuteur : aucune barre latérale à l'impression).
 *
 * « Imprimer / Enregistrer en PDF » ouvre le dialogue d'impression du
 * navigateur, qui produit un PDF au format exact grâce aux pages nommées CSS
 * (`@page jr-tool-card` 63 × 88 mm, `@page jr-sheet` A4). Deux modes :
 * - carte unique : page 1 = recto, page 2 = verso ;
 * - planche A4 : 8 exemplaires, page des versos en miroir (recto-verso bord long).
 * Le bouton reste désactivé tant que le QR n'a pas été décodé avec succès.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { AlertTriangle, ArrowLeft, CheckCircle2, Printer } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { buildCardQrUrl } from '@jeraisonne/db/cards';
import { useCard, type CardRow } from '@/lib/cards/api';
import { checkCardQr } from '@/lib/cards/qr-check';
import { CardRecto, CardVerso } from '@/features/cards/card-faces';

type PrintMode = 'single' | 'sheet';
export const SHEET_COPIES = 9;

function buildFaces(card: CardRow) {
  return {
    recto: (
      <CardRecto
        title={card.title}
        cardType={card.card_type}
        content={card.structured_content}
        levels={card.levels}
        colorTheme={card.color_theme}
      />
    ),
    verso: (
      <CardVerso
        cardId={card.id}
        shortCode={card.short_code}
        title={card.title}
        cardType={card.card_type}
        levels={card.levels}
        xpMax={card.xp_max}
        rarity={card.rarity}
        colorTheme={card.color_theme}
        creature={card.creature}
        domainLabel={card.domain_label}
        content={card.structured_content}
        summaryFallback={card.rule || card.method}
      />
    ),
  };
}

export function TutorCardPrintPage() {
  const { cardId } = useParams({ strict: false }) as { cardId: string };
  const card = useCard(cardId);
  const [mode, setMode] = useState<PrintMode>('single');

  const shortCode = card.data?.short_code;
  const qrUrl = shortCode ? buildCardQrUrl(shortCode) : null;
  const qr = useMemo(
    () => (qrUrl ? checkCardQr(qrUrl) : { ok: false, decoded: null, modules: 0 }),
    [qrUrl],
  );

  if (card.isLoading) return <p className="text-ink-500 p-6 text-sm">Chargement…</p>;
  if (card.error || !card.data) {
    return (
      <p role="alert" className="text-accent-600 p-6 text-sm">
        Carte introuvable.
      </p>
    );
  }

  const faces = buildFaces(card.data);
  const published = card.data.published_version !== null;

  return (
    <div className="jr-print-root px-4 py-6 sm:py-8">
      <div className="jr-print-hide mx-auto mb-6 max-w-3xl space-y-4">
        <Link
          to="/tutor/cards/$cardId"
          params={{ cardId }}
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Retour à la carte
        </Link>
        <h1 className="font-display text-ink-900 text-xl font-extrabold">
          Imprimer « {card.data.title} »
        </h1>

        <div className="flex flex-wrap gap-2" role="group" aria-label="Format d’impression">
          {(
            [
              ['single', 'Carte 63 × 88 (recto + verso)'],
              ['sheet', `Planche A4 · ${SHEET_COPIES} cartes`],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              onClick={() => setMode(value)}
              className={cn(
                'min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold active:scale-[0.98]',
                mode === value
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-ink-200 text-ink-700 bg-white',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <p
          role="status"
          className={cn(
            'flex items-start gap-2 rounded-xl px-3 py-2 text-sm',
            qr.ok ? 'bg-xp-50 text-xp-700' : 'bg-error-50 text-error-600',
          )}
        >
          {qr.ok ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          {qr.ok
            ? `QR code vérifié : il ouvre ${qrUrl}`
            : 'Le QR code n’a pas pu être relu. Impression bloquée.'}
        </p>

        {!published && (
          <p className="bg-accent-50 text-accent-600 rounded-xl px-3 py-2 text-sm">
            Carte non publiée : le QR mènera à une page « pas encore disponible » tant que tu ne
            l’as pas publiée.
          </p>
        )}

        <p className="text-ink-500 text-sm">
          {mode === 'single'
            ? 'Dans le dialogue d’impression : « Enregistrer en PDF » ou ton imprimante, marges « Aucune », échelle 100 %.'
            : 'Imprime en recto-verso « bord long », échelle 100 %, puis découpe le long des pointillés.'}
        </p>

        <Button fullWidth className="sm:w-auto" disabled={!qr.ok} onClick={() => window.print()}>
          <span className="flex items-center justify-center gap-2">
            <Printer className="h-4 w-4" /> Imprimer / Enregistrer en PDF
          </span>
        </Button>
      </div>

      <div className="overflow-x-auto">
        {mode === 'single' ? (
          <div className="flex flex-wrap justify-center gap-6 print:block">
            <div className="jr-print-card-page">{faces.recto}</div>
            <div className="jr-print-card-page">{faces.verso}</div>
          </div>
        ) : (
          <div className="space-y-6 print:space-y-0">
            <div className="jr-print-sheet mx-auto" data-face="recto">
              {Array.from({ length: SHEET_COPIES }, (_, i) => (
                <div key={i} className="jr-print-slot">
                  {faces.recto}
                </div>
              ))}
            </div>
            <div className="jr-print-sheet mx-auto" data-face="verso">
              {Array.from({ length: SHEET_COPIES }, (_, i) => (
                <div key={i} className="jr-print-slot">
                  {faces.verso}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
